import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';

// If a second re-auth attempt fires within this window, treat it as a loop
// (e.g. token audience mismatch, role missing, clock skew) and stop instead
// of bouncing through Auth0 forever.
const LOOP_GUARD_WINDOW_MS = 5000;

@Injectable({ providedIn: 'root' })
export class SessionExpiryService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  // Set while a redirect is in flight so simultaneous 401 responses (e.g.
  // multiple parallel API calls when a token expires) only trigger one
  // loginWithRedirect call. Cleared by reset() when auth recovers.
  private redirecting = false;
  private lastRedirectAt = 0;

  triggerReauth(): void {
    if (this.redirecting) return;

    const now = Date.now();
    if (now - this.lastRedirectAt < LOOP_GUARD_WINDOW_MS) {
      // A redirect just completed and we're already getting 401s again —
      // the SDK got new tokens but the API still rejects them. Sending the
      // user back to Auth0 will just produce the same outcome. Surface the
      // failure instead.
      this.router.navigate(['/access-denied'], { queryParams: { reason: 'auth-loop' } });
      return;
    }

    this.redirecting = true;
    this.lastRedirectAt = now;
    const target = this.router.url;
    this.auth.loginWithRedirect({ appState: { target } });
  }

  reset(): void {
    this.redirecting = false;
  }
}
